import { Inject, Injectable, OnInit } from "@angular/core";
import { AuthClient, AuthResponse } from "./auth.client";
import { ITokenStorageService, TOKEN_STORAGE_SERVICE } from "./itoken.storage";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { catchError, mergeMap, tap, map } from "rxjs/operators";
import { jwtDecode } from "jwt-decode";
import { PERMISSIONS, PermissionValue, getPermissionName } from "../../models/permissions.models";

const PLANT_WAREHOUSE_LETTERS = ["w", "l", "s", "y"] as const;

/**
 * Normalizes JWT `plants` claim to warehouse letters (w|l|s|y).
 * Accepts array, comma/space-separated string, or JSON string.
 */
export function normalizePlantsClaim(raw: unknown): string[] {
    if (raw == null || raw === "") {
        return [];
    }
    let parts: string[] = [];
    if (Array.isArray(raw)) {
        parts = raw.map((x) => String(x).trim()).filter(Boolean);
    } else if (typeof raw === "string") {
        const s = raw.trim();
        if (s.startsWith("[")) {
            try {
                const parsed = JSON.parse(s);
                if (Array.isArray(parsed)) {
                    parts = parsed.map((x) => String(x).trim()).filter(Boolean);
                }
            } catch {
                parts = s.split(/[,;\s]+/).filter(Boolean);
            }
        } else {
            parts = s.split(/[,;\s]+/).filter(Boolean);
        }
    } else {
        parts = [String(raw)];
    }
    const letters = parts
        .map((p) => p.toLowerCase())
        .filter((p) => (PLANT_WAREHOUSE_LETTERS as readonly string[]).includes(p));
    return [...new Set(letters)];
}

export interface UserInfo {
    id: string;
    sub: string;
    email: string;
    roleName: string;
    role?: string;
    permissions?: string[];
    /** Warehouse letters from JWT `plants` claim (e.g. w, l, s, y). */
    plants?: string[];
    azUniqueId?: string;
    plantId?: string;
    plantRef?: string;
    isLightEnabled?: boolean;
    status?: number;
}


@Injectable({ providedIn: 'root' })
export class AuthService {
    public userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
    public userInfo$ = this.userInfoSubject.asObservable();

    constructor(
        private authClient: AuthClient,
        @Inject(TOKEN_STORAGE_SERVICE) private tokenStorage: ITokenStorageService
    ) {
        this.initializeAuthState();
    }

    private initializeAuthState() {
        const refreshToken = this.tokenStorage.getRefreshToken();
        if (refreshToken) {
            this.authClient.refreshTokens(refreshToken).subscribe((authResponse) => {
                // Check status - if 0, clear auth silently (user will see message on login)
                const payload = jwtDecode(authResponse.accessToken) as any;
                const status = payload.status !== undefined ? Number(payload.status) : undefined;
                
                if (status === 0) {
                    // If status is 0, clear auth but don't show error during initialization
                    // The user will see the error when they try to log in
                    this.clearLocalAuth();
                    return;
                }
                
                // Status is OK, store tokens
                this.tokenStorage.setAccessToken(authResponse.accessToken);
                this.tokenStorage.setRefreshToken(authResponse.refreshToken);
                const user = this.getUserInfo();
                this.userInfoSubject.next(user);
            }, (error) => {
                this.clearLocalAuth();
            });
        }
    }

    register(email: string, password: string, companyType: number, companyName?: string): Observable<AuthResponse & { needsVerification?: boolean }> {
        return this.authClient.register(email, password, companyType, companyName).pipe(
            map((response) => {
                // Check status - if 0, mark as needing verification but don't store tokens
                const payload = jwtDecode(response.accessToken) as any;
                const status = payload.status !== undefined ? Number(payload.status) : undefined;
                
                if (status === 0) {
                    // Don't store tokens, but return response with flag
                    console.log('User status is 0 - account needs verification');
                    return { ...response, needsVerification: true };
                }
                
                // Status is OK, store tokens
                this.handleAuthSuccess(response);
                return { ...response, needsVerification: false };
            }),
            catchError((error) => {
                return this.handleAuthError(error);
            })
        );
    }

    login(email: string, password: string): Observable<AuthResponse & { needsVerification?: boolean }> {
        return this.authClient.login(email, password).pipe(
            map((response) => {
                // Check status - if 0, mark as needing verification but don't store tokens
                const payload = jwtDecode(response.accessToken) as any;
                const status = payload.status !== undefined ? Number(payload.status) : undefined;
                
                if (status === 0) {
                    // Don't store tokens, but return response with flag
                    console.log('User status is 0 - account needs verification');
                    return { ...response, needsVerification: true };
                }
                
                // Status is OK, store tokens
                this.handleAuthSuccess(response);
                return { ...response, needsVerification: false };
            }),
            catchError((error) => {
                return this.handleAuthError(error);
            })
        );
    }

    refreshTokens(): Observable<AuthResponse & { needsVerification?: boolean }> {
        const refreshToken = this.tokenStorage.getRefreshToken();

        if (!refreshToken) {
            this.clearLocalAuth();
            return throwError('No refresh token found');
        }

        return this.authClient.refreshTokens(refreshToken).pipe(
            map((response) => {
                // Check status - if 0, mark as needing verification but don't store tokens
                const payload = jwtDecode(response.accessToken) as any;
                const status = payload.status !== undefined ? Number(payload.status) : undefined;
                
                if (status === 0) {
                    // Don't store tokens, clear auth
                    console.log('User status is 0 - account needs verification');
                    this.clearLocalAuth();
                    return { ...response, needsVerification: true };
                }
                
                // Status is OK, store tokens
                this.handleRefreshSuccess(response);
                return { ...response, needsVerification: false };
            }),
            catchError((error) => {
                return this.handleAuthError(error);
            })
        );
    }

    logout(): Observable<void> {
        const refreshToken = this.tokenStorage.getRefreshToken();

        if (!refreshToken) {
            this.clearLocalAuth();
            return throwError('No refresh token found');
        }

        return this.authClient.logout(refreshToken).pipe(
            tap(() => this.clearLocalAuth()),
            catchError((error) => {
                this.handleAuthError(error);
                return throwError(error);
            })
        );
    }

    exchangeToken(azureToken: string): Observable<AuthResponse & { needsVerification?: boolean }> {
        return this.authClient.exchangeAzureToken(azureToken).pipe(
            map((response) => {
                // Check status - if 0, mark as needing verification but don't store tokens
                const payload = jwtDecode(response.accessToken) as any;
                const status = payload.status !== undefined ? Number(payload.status) : undefined;
                
                if (status === 0) {
                    // Don't store tokens, but return response with flag
                    console.log('User status is 0 - account needs verification');
                    return { ...response, needsVerification: true };
                }
                
                // Status is OK, store tokens
                this.handleAuthSuccess(response);
                return { ...response, needsVerification: false };
            }),
            catchError((error) => {
                return this.handleAuthError(error);
            })
        );
    }

    isAuthenticated(): boolean {
        const accessToken = this.tokenStorage.getAccessToken();
        const refreshToken = this.tokenStorage.getRefreshToken();
        return !!(accessToken && refreshToken);
    }

    hasRefreshToken(): boolean {
        return Boolean(this.tokenStorage.getRefreshToken());
    }

    getAccessToken(): string | null {
        return this.tokenStorage.getAccessToken();
    }

    getUserInfo(): UserInfo | null {
        const accessToken = this.tokenStorage.getAccessToken();
        if (!accessToken) {
            return null;
        }
        const payload = jwtDecode(accessToken) as any;
        
        // Check user status - status 0 means account is not verified
        const status = payload.status !== undefined ? Number(payload.status) : undefined;
        if (status === 0) {
            // Clear tokens and return null to prevent access
            this.clearLocalAuth();
            return null;
        }
        
        // Handle permission array - JWT may have 'permission' as array or single value (string or number)
        let permissionsRaw: any[] = [];
        if (payload.permission) {
            permissionsRaw = Array.isArray(payload.permission) ? payload.permission : [payload.permission];
        }

        let permissions: string[] = permissionsRaw
            .map((p) => {
                if (typeof p === "number") {
                    return getPermissionName(p as PermissionValue) || String(p);
                }
                return String(p);
            })
            .map((p) => p.trim())
            .filter(Boolean)
            .map((p) => {
                // Backward-compat aliases from older JWTs / backend naming
                if (p === "PalletManage") return PERMISSIONS.PalletsManage;
                if (p === "PalletView") return PERMISSIONS.PalletsView;
                return p;
            });
        
        // SuperUser automatically has all permissions
        if (payload.role === "SuperUser" || payload.role === "1") {
            permissions = Object.values(PERMISSIONS);
        }

        const plants = normalizePlantsClaim(payload.plants);
        
        return {
            ...payload,
            roleName: payload.role || payload.roleName,
            role: payload.role,
            permissions: permissions,
            plants: plants.length > 0 ? plants : undefined,
            status: status
        } as UserInfo;
    }

    /**
     * Check if user has a specific permission
     */
    hasPermission(permission: string): boolean {
        const userInfo = this.getUserInfo();
        if (!userInfo) {
            return false;
        }
        
        // SuperUser has all permissions
        if (userInfo.role === "SuperUser" || userInfo.roleName === "SuperUser") {
            return true;
        }
        
        return userInfo.permissions?.includes(permission) || false;
    }

    /**
     * Check if user has any of the specified permissions
     */
    hasAnyPermission(permissions: string[]): boolean {
        if (!permissions || permissions.length === 0) {
            return false;
        }
        
        const userInfo = this.getUserInfo();
        if (!userInfo) {
            return false;
        }
        
        // SuperUser has all permissions
        if (userInfo.role === "SuperUser" || userInfo.roleName === "SuperUser") {
            return true;
        }
        
        return permissions.some(permission => userInfo.permissions?.includes(permission)) || false;
    }

    /**
     * Check if user has all of the specified permissions
     */
    hasAllPermissions(permissions: string[]): boolean {
        if (!permissions || permissions.length === 0) {
            return false;
        }
        
        const userInfo = this.getUserInfo();
        if (!userInfo) {
            return false;
        }
        
        // SuperUser has all permissions
        if (userInfo.role === "SuperUser" || userInfo.roleName === "SuperUser") {
            return true;
        }
        
        return permissions.every(permission => userInfo.permissions?.includes(permission)) || false;
    }

    /**
     * Check if user is a SuperUser
     */
    isSuperUser(): boolean {
        const userInfo = this.getUserInfo();
        if (!userInfo) {
            console.log('isSuperUser: No user info');
            return false;
        }
        // role can come from JWT as a string (e.g. "SuperUser" or "1"). Normalize to string for safety.
        const roleStr = String(userInfo.role ?? '');
        const isSuper =
            roleStr === "SuperUser" ||
            roleStr === "1" ||
            userInfo.roleName === "SuperUser";
        console.log('isSuperUser check:', { role: userInfo.role, roleName: userInfo.roleName, isSuper });
        return isSuper;
    }

    private handleAuthSuccess(response: AuthResponse): void {
        this.tokenStorage.setAccessToken(response.accessToken);
        this.tokenStorage.setRefreshToken(response.refreshToken);
        const user = this.getUserInfo();
        this.userInfoSubject.next(user);
    }
    
    private checkUserStatus(response: AuthResponse): void {
        // Decode token to check status before storing
        const payload = jwtDecode(response.accessToken) as any;
        const status = payload.status !== undefined ? Number(payload.status) : undefined;
        
        console.log('Checking user status:', status, 'from payload:', payload);
        
        if (status === 0) {
            console.log('User status is 0 - account needs verification');
            this.clearLocalAuth();
            const error = new Error('Your account needs to be verified by an administrator before you can access the system. Please contact your administrator.');
            throw error;
        }
    }

    private handleRefreshSuccess(response: AuthResponse): void {
        this.tokenStorage.setAccessToken(response.accessToken);
        if (response.refreshToken) {
            this.tokenStorage.setRefreshToken(response.refreshToken);
        }
        const user = this.getUserInfo();
        this.userInfoSubject.next(user);
    }

    private clearLocalAuth(): void {
        this.tokenStorage.clearAllTokens();
        this.userInfoSubject.next(null);
    }

    private handleAuthError(error: any): Observable<never> {
        console.error('Authentication error:', error);
        return throwError(error);
    }
}